import React from 'react';
import styled from 'styled-components';
import { useLanguage } from './Header';

const LCSection = styled.div`
  padding: 2rem 2rem;
  border-bottom: 1px solid rgba(17, 24, 39, 0.08);
  transition: background-color 0.2s ease;
  
  &:last-child { 
    border-bottom: none; 
  }
  
  &:hover {
    background-color: #fafbfc;
  }
  
  .dark & { 
    border-bottom-color: rgba(71, 85, 105, 0.3);
    
    &:hover {
      background-color: rgba(255, 255, 255, 0.02);
    }
  }
  
  @media (max-width: 768px) {
    padding: 1.5rem 1.5rem;
  }
`;

const LCSectionHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1.25rem;
  gap: 1rem;
`;

const LCSectionNumber = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  background: #0f172a;
  color: #ffffff;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 800;
  flex-shrink: 0;
  
  .dark & { 
    background: #f1f5f9; 
    color: #0f172a; 
  }
`;

const LCSectionTitle = styled.h3`
  color: #0f172a;
  margin: 0;
  font-size: 1.35rem;
  font-weight: 800;
  letter-spacing: -0.02em;
  line-height: 1.3;
  
  .dark & { 
    color: #f1f5f9; 
  }
  
  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
`;

const LCSectionBody = styled.div`
  line-height: 1.75;
  color: #475569;
  font-size: 1rem;
  padding-left: 3.5rem;
  
  p { 
    margin-bottom: 1.25rem; 
    color: #64748b;
    line-height: 1.75;
  }
  
  ul { 
    margin: 1.25rem 0; 
    padding-left: 1.5rem; 
    list-style-type: disc;
  }
  
  li { 
    margin-bottom: 0.875rem; 
  line-height: 1.7;
    color: #64748b;
  }
  
  strong { 
    color: #0f172a; 
    font-weight: 700; 
  }
  
  .dark & { 
    color: #94a3b8;
    
    p {
      color: #94a3b8;
    }
    
    li {
      color: #94a3b8;
    }
  }
  
  .dark & strong { 
    color: #e2e8f0; 
  }
  
  @media (max-width: 768px) {
    padding-left: 0;
    font-size: 0.9375rem;
  }
`;

const LCContactHighlight = styled.span`
  color: #0f172a;
  font-weight: 700;
  background: #f8fafc;
  padding: 0.375rem 0.625rem;
  border-radius: 8px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, monospace;
  border: 1px solid rgba(17, 24, 39, 0.1);
  
  .dark & { 
    color: #f1f5f9; 
    background: rgba(241, 245, 249, 0.1);
    border-color: rgba(71, 85, 105, 0.4);
  }
`;

export const TermsContent: React.FC = () => {
  const { language } = useLanguage();
  if (language === 'fr') {
    return (
      <div>
        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>1</LCSectionNumber>
            <LCSectionTitle>Dispositions générales</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <p>Ces conditions s’appliquent à tous les services et produits proposés par <strong>Phone Bros</strong>. En utilisant nos services de reprise et de vente, vous acceptez ces conditions.</p>
            <p>Nous pouvons mettre ces conditions à jour. La version la plus récente est toujours disponible sur notre site.</p>
          </LCSectionBody>
        </LCSection>

        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>2</LCSectionNumber>
            <LCSectionTitle>Services de reprise et de vente</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <p>Phone Bros propose des services de reprise pour mobiles, tablettes, ordinateurs portables et autres appareils électroniques. Nos services incluent :</p>
            <ul>
              <li><strong>Évaluation instantanée :</strong> Offre transparente selon les spécifications</li>
              <li><strong>Contrôle technique :</strong> Qualité vérifiée par des techniciens certifiés</li>
              <li><strong>Effacement des données :</strong> Certifié et irréversible</li>
              <li><strong>Paiement :</strong> Sous 24 heures après approbation</li>
            </ul>
            <p>Nous visons une valeur de marché juste pour votre appareil.</p>
          </LCSectionBody>
        </LCSection>

        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>3</LCSectionNumber>
            <LCSectionTitle>Prix et évaluation</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <p>Toutes les offres sont <strong>sans engagement</strong> et basées sur vos informations. Le prix final est déterminé après inspection.</p>
            <p><strong>Ajustements possibles</strong> si :</p>
            <ul>
              <li>L’appareil ne correspond pas aux informations fournies</li>
              <li>Des dommages non signalés sont constatés</li>
              <li>L’appareil n’est pas pleinement fonctionnel</li>
            </ul>
            <p>Vous pouvez refuser l’offre révisée et récupérer votre appareil.</p>
          </LCSectionBody>
        </LCSection>

        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>4</LCSectionNumber>
            <LCSectionTitle>Paiement</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <p>Après acceptation, paiement sous <strong>24 heures</strong> via :</p>
            <ul>
              <li>Virement bancaire</li>
              <li>Avoir en magasin</li>
              <li>Remise immédiate</li>
            </ul>
            <p>Montants TVA comprise si applicable.</p>
          </LCSectionBody>
        </LCSection>

        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>5</LCSectionNumber>
            <LCSectionTitle>Garantie et qualité</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <p>Évaluation <strong>juste et transparente</strong> par des techniciens certifiés selon les normes du secteur.</p>
          </LCSectionBody>
        </LCSection>

        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>6</LCSectionNumber>
            <LCSectionTitle>Vie privée et protection des données</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <ul>
              <li><strong>Effacement :</strong> Données personnelles effacées de manière irréversible</li>
              <li><strong>Conformité :</strong> RGPD et droit belge</li>
            </ul>
          </LCSectionBody>
        </LCSection>

        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>7</LCSectionNumber>
            <LCSectionTitle>Responsabilité</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <p>Responsabilité limitée aux dommages directs résultant d’un manquement imputable.</p>
            <p>Plafond : la valeur de reprise de l’appareil concerné.</p>
          </LCSectionBody>
        </LCSection>

        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>8</LCSectionNumber>
            <LCSectionTitle>Contact et litiges</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <p>Contact : <LCContactHighlight>info@phonebros.be</LCContactHighlight> — <LCContactHighlight>+32 484 89 90 32</LCContactHighlight>.</p>
            <p>Droit applicable : <strong>belge</strong>. Juridictions compétentes.</p>
          </LCSectionBody>
        </LCSection>
      </div>
    );
  }
  if (language === 'de') {
    return (
      <div>
        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>1</LCSectionNumber>
            <LCSectionTitle>Allgemeine Bestimmungen</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <p>Diese Bedingungen gelten für alle von <strong>Phone Bros</strong> angebotenen Dienste und Produkte. Durch Nutzung unserer Ankaufs‑/Verkaufsdienste stimmen Sie zu.</p>
            <p>Wir können die Bedingungen aktualisieren. Die aktuelle Version steht auf unserer Website.</p>
          </LCSectionBody>
        </LCSection>

        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>2</LCSectionNumber>
            <LCSectionTitle>Ankauf- und Verkaufsdienste</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <ul>
              <li><strong>Sofortbewertung:</strong> Transparentes Angebot nach Gerätespezifikationen</li>
              <li><strong>Technische Prüfung:</strong> Zertifizierte Qualitätskontrolle</li>
              <li><strong>Datenlöschung:</strong> Zertifiziert und unumkehrbar</li>
              <li><strong>Auszahlung:</strong> Innerhalb von 24 Stunden nach Freigabe</li>
            </ul>
          </LCSectionBody>
        </LCSection>

        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>3</LCSectionNumber>
            <LCSectionTitle>Preis und Bewertung</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <p>Angebote sind <strong>unverbindlich</strong> und basieren auf Ihren Angaben; Endpreis nach Prüfung.</p>
            <p>Anpassungen, wenn Gerät nicht den Angaben entspricht, Schäden vorliegen oder es nicht voll funktionsfähig ist.</p>
          </LCSectionBody>
        </LCSection>

        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>4</LCSectionNumber>
            <LCSectionTitle>Zahlung</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <ul>
              <li>Überweisung</li>
              <li>Gutschrift im Shop</li>
              <li>Sofortrabatt</li>
            </ul>
          </LCSectionBody>
        </LCSection>

        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>5</LCSectionNumber>
            <LCSectionTitle>Gewährleistung und Qualität</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <p>Faire, <strong>transparente</strong> Bewertung durch zertifizierte Techniker.</p>
          </LCSectionBody>
        </LCSection>

        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>6</LCSectionNumber>
            <LCSectionTitle>Datenschutz</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <ul>
              <li><strong>Datenlöschung</strong></li>
              <li><strong>DSGVO</strong> und belgisches Recht</li>
            </ul>
          </LCSectionBody>
        </LCSection>

        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>7</LCSectionNumber>
            <LCSectionTitle>Haftung</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <p>Beschränkt auf direkte Schäden; Höchstgrenze ist der Ankaufswert des Geräts.</p>
          </LCSectionBody>
        </LCSection>

        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>8</LCSectionNumber>
            <LCSectionTitle>Kontakt und Streitigkeiten</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <p>Kontakt: <LCContactHighlight>info@phonebros.be</LCContactHighlight> — <LCContactHighlight>+32 484 89 90 32</LCContactHighlight>.</p>
            <p>Geltendes Recht: <strong>Belgien</strong>.</p>
          </LCSectionBody>
        </LCSection>
      </div>
    );
  }
  if (language === 'es') {
    return (
      <div>
        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>1</LCSectionNumber>
            <LCSectionTitle>Disposiciones generales</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <p>Estas condiciones se aplican a todos los servicios y productos de <strong>Phone Bros</strong>. Al usar nuestros servicios de recompra/venta, acepta estas condiciones.</p>
            <p>Podemos actualizarlas; la versión vigente está en nuestro sitio web.</p>
          </LCSectionBody>
        </LCSection>

        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>2</LCSectionNumber>
            <LCSectionTitle>Servicios de recompra y venta</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <ul>
              <li><strong>Valoración instantánea</strong></li>
              <li><strong>Inspección técnica</strong></li>
              <li><strong>Borrado de datos</strong> certificado e irreversible</li>
              <li><strong>Pago</strong> en 24 h tras la aprobación</li>
            </ul>
          </LCSectionBody>
        </LCSection>

        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>3</LCSectionNumber>
            <LCSectionTitle>Precio y valoración</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <p>Ofertas <strong>no vinculantes</strong>; precio final tras inspección. Ajustes si no coincide con lo declarado, hay daños o no funciona plenamente.</p>
          </LCSectionBody>
        </LCSection>

        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>4</LCSectionNumber>
            <LCSectionTitle>Pago</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <ul>
              <li>Transferencia</li>
              <li>Crédito en tienda</li>
              <li>Descuento inmediato</li>
            </ul>
          </LCSectionBody>
        </LCSection>

        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>5</LCSectionNumber>
            <LCSectionTitle>Garantía y calidad</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <p>Valoración <strong>justa y transparente</strong> por técnicos certificados.</p>
          </LCSectionBody>
        </LCSection>

        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>6</LCSectionNumber>
            <LCSectionTitle>Privacidad y protección de datos</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <ul>
              <li>Borrado de datos</li>
              <li>Cumplimiento RGPD y ley belga</li>
            </ul>
          </LCSectionBody>
        </LCSection>

        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>7</LCSectionNumber>
            <LCSectionTitle>Responsabilidad</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <p>Limitada a daños directos; tope: valor de recompra.</p>
          </LCSectionBody>
        </LCSection>

        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>8</LCSectionNumber>
            <LCSectionTitle>Contacto y litigios</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <p><LCContactHighlight>info@phonebros.be</LCContactHighlight> — <LCContactHighlight>+32 484 89 90 32</LCContactHighlight>.</p>
            <p>Ley aplicable: <strong>belga</strong>.</p>
          </LCSectionBody>
        </LCSection>
      </div>
    );
  }
  if (language === 'bg') {
    return (
      <div>
        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>1</LCSectionNumber>
            <LCSectionTitle>Общи условия</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <p>Тези условия важат за всички услуги и продукти на <strong>Phone Bros</strong>. С използването на нашите услуги Вие ги приемате.</p>
            <p>Възможни са актуализации; най‑новата версия е на сайта.</p>
          </LCSectionBody>
        </LCSection>

        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>2</LCSectionNumber>
            <LCSectionTitle>Услуги за изкупуване и продажба</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <ul>
              <li><strong>Моментна оценка</strong></li>
              <li><strong>Техническа проверка</strong></li>
              <li><strong>Изтриване на данни</strong> – сертифицирано</li>
              <li><strong>Плащане</strong> до 24 ч. след одобрение</li>
            </ul>
          </LCSectionBody>
        </LCSection>

        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>3</LCSectionNumber>
            <LCSectionTitle>Цена и оценка</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <p>Офертите са <strong>необвързващи</strong>; крайна цена след оглед. Корекции при несъответствия, щети или неизправност.</p>
          </LCSectionBody>
        </LCSection>

        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>4</LCSectionNumber>
            <LCSectionTitle>Плащане</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <ul>
              <li>Банков превод</li>
              <li>Кредит в магазин</li>
              <li>Незабавна отстъпка</li>
            </ul>
          </LCSectionBody>
        </LCSection>

        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>5</LCSectionNumber>
            <LCSectionTitle>Гаранция и качество</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <p><strong>Честна и прозрачна</strong> оценка от сертифицирани техници.</p>
          </LCSectionBody>
        </LCSection>

        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>6</LCSectionNumber>
            <LCSectionTitle>Поверителност и защита на данните</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <ul>
              <li>Безвъзвратно изтриване на данни</li>
              <li>Съответствие с GDPR и белгийско право</li>
            </ul>
          </LCSectionBody>
        </LCSection>

        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>7</LCSectionNumber>
            <LCSectionTitle>Отговорност</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <p>Ограничена до преки вреди; максимум – стойността на изкупуване.</p>
          </LCSectionBody>
        </LCSection>

        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>8</LCSectionNumber>
            <LCSectionTitle>Контакт и спорове</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <p><LCContactHighlight>info@phonebros.be</LCContactHighlight> — <LCContactHighlight>+32 484 89 90 32</LCContactHighlight>.</p>
            <p>Приложимо право: <strong>Белгия</strong>.</p>
          </LCSectionBody>
        </LCSection>
      </div>
    );
  }
  if (language === 'ro') {
    return (
      <div>
        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>1</LCSectionNumber>
            <LCSectionTitle>Dispoziții generale</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <p>Aceste condiții se aplică tuturor serviciilor și produselor <strong>Phone Bros</strong>. Folosind serviciile noastre, acceptați aceste condiții.</p>
            <p>Pot apărea actualizări; versiunea curentă este pe site.</p>
          </LCSectionBody>
        </LCSection>

        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>2</LCSectionNumber>
            <LCSectionTitle>Servicii de buy‑back și vânzare</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <ul>
              <li><strong>Evaluare instant</strong></li>
              <li><strong>Inspecție tehnică</strong></li>
              <li><strong>Ștergere date</strong> certificată</li>
              <li><strong>Plată</strong> în 24 h după aprobare</li>
            </ul>
          </LCSectionBody>
        </LCSection>

        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>3</LCSectionNumber>
            <LCSectionTitle>Preț și evaluare</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <p>Oferte <strong>neobligatorii</strong>; preț final după inspecție. Ajustări la neconcordanțe, daune sau nefuncționare.</p>
          </LCSectionBody>
        </LCSection>

        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>4</LCSectionNumber>
            <LCSectionTitle>Plată</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <ul>
              <li>Transfer bancar</li>
              <li>Credit în magazin</li>
              <li>Reducere imediată</li>
            </ul>
          </LCSectionBody>
        </LCSection>

        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>5</LCSectionNumber>
            <LCSectionTitle>Garanție și calitate</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <p>Evaluare <strong>corectă și transparentă</strong> de către tehnicieni certificați.</p>
          </LCSectionBody>
        </LCSection>

        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>6</LCSectionNumber>
            <LCSectionTitle>Confidențialitate și protecția datelor</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <ul>
              <li>Ștergere ireversibilă a datelor</li>
              <li>Conformitate GDPR și lege belgiană</li>
            </ul>
          </LCSectionBody>
        </LCSection>

        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>7</LCSectionNumber>
            <LCSectionTitle>Răspundere</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <p>Limitată la daune directe; plafon: valoarea de buy‑back.</p>
          </LCSectionBody>
        </LCSection>

        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>8</LCSectionNumber>
            <LCSectionTitle>Contact și litigii</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <p><LCContactHighlight>info@phonebros.be</LCContactHighlight> — <LCContactHighlight>+32 484 89 90 32</LCContactHighlight>.</p>
            <p>Lege aplicabilă: <strong>belgiană</strong>.</p>
          </LCSectionBody>
        </LCSection>
      </div>
    );
  }
  if (language === 'sk') {
    return (
      <div>
        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>1</LCSectionNumber>
            <LCSectionTitle>Všeobecné ustanovenia</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <p>Tieto podmienky sa vzťahujú na všetky služby a produkty <strong>Phone Bros</strong>. Používaním služieb ich prijímate.</p>
            <p>Aktualizácie sú možné; aktuálna verzia je na webe.</p>
          </LCSectionBody>
        </LCSection>

        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>2</LCSectionNumber>
            <LCSectionTitle>Výkup a predaj</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <ul>
              <li><strong>Okamžité ocenenie</strong></li>
              <li><strong>Technická kontrola</strong></li>
              <li><strong>Mazanie dát</strong> – certifikované</li>
              <li><strong>Platba</strong> do 24 h po schválení</li>
            </ul>
          </LCSectionBody>
        </LCSection>

        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>3</LCSectionNumber>
            <LCSectionTitle>Cena a ocenenie</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <p>Ponuky sú <strong>nezáväzné</strong>; konečná cena po kontrole. Úpravy pri nezhodách, poškodení alebo nefunkčnosti.</p>
          </LCSectionBody>
        </LCSection>

        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>4</LCSectionNumber>
            <LCSectionTitle>Platba</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <ul>
              <li>Bankový prevod</li>
              <li>Kredit v predajni</li>
              <li>Okamžitá zľava</li>
            </ul>
          </LCSectionBody>
        </LCSection>

        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>5</LCSectionNumber>
            <LCSectionTitle>Záruka a kvalita</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <p><strong>Spravodlivé a transparentné</strong> ocenenie certifikovanými technikmi.</p>
          </LCSectionBody>
        </LCSection>

        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>6</LCSectionNumber>
            <LCSectionTitle>Ochrana súkromia</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <ul>
              <li>Nezvratné mazanie dát</li>
              <li>Súlad s GDPR a belgickým právom</li>
            </ul>
          </LCSectionBody>
        </LCSection>

        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>7</LCSectionNumber>
            <LCSectionTitle>Zodpovednosť</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <p>Obmedzená na priame škody; strop: výkupná hodnota.</p>
          </LCSectionBody>
        </LCSection>

        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>8</LCSectionNumber>
            <LCSectionTitle>Kontakt a spory</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <p><LCContactHighlight>info@phonebros.be</LCContactHighlight> — <LCContactHighlight>+32 484 89 90 32</LCContactHighlight>.</p>
            <p>Uplatňuje sa <strong>belgické právo</strong>.</p>
          </LCSectionBody>
        </LCSection>
      </div>
    );
  }
  if (language === 'tr') {
    return (
      <div>
        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>1</LCSectionNumber>
            <LCSectionTitle>Genel hükümler</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <p>Bu şartlar ve koşullar <strong>Phone Bros</strong> tarafından sunulan tüm hizmet ve ürünler için geçerlidir. Takas ve satış hizmetlerimizi kullanarak bu şartları kabul etmiş olursunuz.</p>
            <p>Şartlar zaman zaman güncellenebilir. En güncel sürüm her zaman web sitemizde yayımlanır.</p>
          </LCSectionBody>
        </LCSection>

        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>2</LCSectionNumber>
            <LCSectionTitle>Takas ve satış hizmetleri</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <p>Phone Bros; mobil cihazlar, tabletler, dizüstü bilgisayarlar ve diğer elektronikler için takas hizmeti sunar. Hizmetlerimiz şunları içerir:</p>
            <ul>
              <li><strong>Anında değerleme:</strong> Cihaz özelliklerine göre şeffaf fiyat teklifi</li>
              <li><strong>Teknik kontrol:</strong> Sertifikalı teknisyenler tarafından kalite kontrol</li>
              <li><strong>Veri silme:</strong> Sertifikalı ve geri döndürülemez veri temizleme</li>
              <li><strong>Ödeme:</strong> Onaydan sonra 24 saat içinde ödeme</li>
            </ul>
            <p>Cihazınız için adil bir piyasa değeri sunmaya özen gösteririz.</p>
          </LCSectionBody>
        </LCSection>

        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>3</LCSectionNumber>
            <LCSectionTitle>Fiyatlandırma ve değerleme</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <p>Tüm fiyat teklifleri <strong>bağlayıcı değildir</strong> ve tarafınızca verilen bilgilere dayanır. Nihai fiyat cihaz incelemesinden sonra belirlenir.</p>
            <p><strong>Fiyat güncellemeleri</strong> şu hallerde uygulanabilir:</p>
            <ul>
              <li>Cihaz belirtilen özelliklerle uyuşmadığında</li>
              <li>Bildirilmemiş hasar veya arıza tespit edildiğinde</li>
              <li>Cihaz tam olarak çalışır durumda olmadığında</li>
            </ul>
            <p>Düzeltilmiş teklifi reddetme ve cihazınızı geri alma hakkınız her zaman saklıdır.</p>
          </LCSectionBody>
        </LCSection>

        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>4</LCSectionNumber>
            <LCSectionTitle>Ödeme ve havale</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <p>Nihai teklifin kabulünden sonra ödeme <strong>24 saat</strong> içinde aşağıdaki yollarla yapılır:</p>
            <ul>
              <li>Belirttiğiniz banka hesabına havale</li>
              <li>Mağaza kredisi (alışverişte kullanılmak üzere)</li>
              <li>Yeni bir alışta anında indirim</li>
            </ul>
            <p>Uygulandığı durumlarda tüm tutarlara KDV dahildir.</p>
          </LCSectionBody>
        </LCSection>

        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>5</LCSectionNumber>
            <LCSectionTitle>Garanti ve kalite</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <p>Cihazınız için <strong>adil ve şeffaf</strong> bir değerleme garanti ederiz. Kalite kontrolü, sektör standartlarına göre sertifikalı teknisyenlerce gerçekleştirilir.</p>
          </LCSectionBody>
        </LCSection>

        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>6</LCSectionNumber>
            <LCSectionTitle>Gizlilik ve veri koruma</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <p>Gizliliğiniz esastır. Şunları sağlarız:</p>
            <ul>
              <li><strong>Veri silme:</strong> Kişisel veriler geri döndürülemez şekilde silinir</li>
              <li><strong>Mevzuata uygunluk:</strong> GDPR ve Belçika hukukuna tam uyum</li>
            </ul>
          </LCSectionBody>
        </LCSection>

        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>7</LCSectionNumber>
            <LCSectionTitle>Sorumluluk</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <p>Phone Bros, yükümlülüklerimizin ihlalinden doğrudan kaynaklanan doğrudan zararlarla sınırlı olmak üzere sorumludur.</p>
            <p>Sorumluluğumuz her durumda ilgili cihazın takas değeri ile sınırlıdır.</p>
          </LCSectionBody>
        </LCSection>

        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>8</LCSectionNumber>
            <LCSectionTitle>İletişim ve uyuşmazlıklar</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <p>Sorularınız veya şikayetleriniz için bize <LCContactHighlight>info@phonebros.be</LCContactHighlight> ya da <LCContactHighlight>+32 484 89 90 32</LCContactHighlight> üzerinden ulaşabilirsiniz. Taleplerinizi hızlı ve doğru şekilde yanıtlarız.</p>
            <p>Bu sözleşme <strong>Belçika hukukuna</strong> tabidir. Uyuşmazlıklar yetkili mahkemelerde görülür.</p>
          </LCSectionBody>
        </LCSection>
      </div>
    );
  }
  if (language !== 'nl') {
    return (
      <div>
        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>1</LCSectionNumber>
            <LCSectionTitle>General provisions</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <p>These terms apply to all services and products offered by <strong>Phone Bros</strong>. By using our trade‑in and selling services, you agree to these terms.</p>
            <p>We may update these terms. The most recent version is always available on our website.</p>
          </LCSectionBody>
        </LCSection>

        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>2</LCSectionNumber>
            <LCSectionTitle>Trade‑in and selling services</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <p>Phone Bros provides trade‑in services for mobile devices, tablets, laptops and other electronics. Our services include:</p>
            <ul>
              <li><strong>Instant valuation:</strong> Transparent quote based on device specifications</li>
              <li><strong>Technical inspection:</strong> Quality control by certified technicians</li>
              <li><strong>Data erasure:</strong> Certified and irreversible data wiping</li>
              <li><strong>Payout:</strong> Payment within 24 hours after approval</li>
            </ul>
            <p>We aim to offer a fair market value for your device.</p>
          </LCSectionBody>
        </LCSection>

        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>3</LCSectionNumber>
            <LCSectionTitle>Pricing and valuation</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <p>All quotes are <strong>non‑binding</strong> and based on the information you provide. The final price is determined after inspecting the device.</p>
            <p><strong>Price adjustments</strong> may apply if:</p>
            <ul>
              <li>The device does not match the provided specifications</li>
              <li>Unreported damage or defects are found</li>
              <li>The device is not fully functional</li>
            </ul>
            <p>You always have the right to refuse an adjusted quote and have your device returned.</p>
          </LCSectionBody>
        </LCSection>

        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>4</LCSectionNumber>
            <LCSectionTitle>Payment and payout</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <p>After accepting the final quote, the payment is processed within <strong>24 hours</strong> via:</p>
            <ul>
              <li>Bank transfer to your provided account</li>
              <li>Store credit for use in our shop</li>
              <li>Immediate discount on a new purchase</li>
            </ul>
            <p>All amounts include VAT where applicable.</p>
          </LCSectionBody>
        </LCSection>

        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>5</LCSectionNumber>
            <LCSectionTitle>Warranty and quality</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <p>We guarantee a <strong>fair and transparent</strong> valuation of your device. Our quality control is performed by certified technicians in line with industry standards.</p>
          </LCSectionBody>
        </LCSection>

        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>6</LCSectionNumber>
            <LCSectionTitle>Privacy and data protection</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <p>Your privacy is paramount. We ensure:</p>
            <ul>
              <li><strong>Data erasure:</strong> Personal data is irreversibly wiped</li>
              <li><strong>Applicable regulations:</strong> GDPR and Belgian law compliance</li>
            </ul>
          </LCSectionBody>
        </LCSection>

        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>7</LCSectionNumber>
            <LCSectionTitle>Liability</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <p>Phone Bros is only liable for direct damages that are the direct result of a culpable breach of our obligations.</p>
            <p>Our liability is in all cases limited to the trade‑in value of the relevant device.</p>
          </LCSectionBody>
        </LCSection>

        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>8</LCSectionNumber>
            <LCSectionTitle>Contact and disputes</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <p>For questions or complaints, contact us at <LCContactHighlight>info@phonebros.be</LCContactHighlight> or <LCContactHighlight>+32 484 89 90 32</LCContactHighlight>. We aim for swift and proper handling.</p>
            <p>This agreement is governed by <strong>Belgian law</strong>. Disputes are submitted to the competent courts.</p>
          </LCSectionBody>
        </LCSection>
      </div>
    );
  }

  return (
    <div>
      <LCSection>
        <LCSectionHeader>
          <LCSectionNumber>1</LCSectionNumber>
          <LCSectionTitle>Algemene bepalingen</LCSectionTitle>
        </LCSectionHeader>
        <LCSectionBody>
          <p>Deze voorwaarden zijn van toepassing op alle diensten en producten aangeboden door <strong>Phone Bros</strong>. Door gebruik te maken van onze inruil- en verkoopdiensten, gaat u akkoord met deze voorwaarden.</p>
          <p>Wij kunnen deze voorwaarden aanpassen. De meest recente versie is steeds raadpleegbaar op onze website.</p>
        </LCSectionBody>
      </LCSection>

    <LCSection>
      <LCSectionHeader>
        <LCSectionNumber>2</LCSectionNumber>
        <LCSectionTitle>Inruil- en verkoopdiensten</LCSectionTitle>
      </LCSectionHeader>
      <LCSectionBody>
        <p>Phone Bros biedt inruildiensten voor mobiele apparaten, tablets, laptops en andere elektronische toestellen. Onze diensten omvatten:</p>
        <ul>
          <li><strong>Directe waardering:</strong> Transparante prijsopgave op basis van toestel‑specificaties</li>
          <li><strong>Technische controle:</strong> Kwaliteitscontrole door gecertificeerde technici</li>
          <li><strong>Dataverwijdering:</strong> Gecertificeerde en onherstelbare wissing van gegevens</li>
          <li><strong>Uitbetaling:</strong> Betaling binnen 24 uur na goedkeuring</li>
        </ul>
        <p>Wij streven ernaar een correcte marktwaarde voor uw toestel te bieden.</p>
      </LCSectionBody>
    </LCSection>

    <LCSection>
      <LCSectionHeader>
        <LCSectionNumber>3</LCSectionNumber>
        <LCSectionTitle>Prijzen en waardering</LCSectionTitle>
      </LCSectionHeader>
      <LCSectionBody>
        <p>Alle prijsopgaves zijn <strong>vrijblijvend</strong> en gebaseerd op de door u verstrekte informatie. De definitieve prijs wordt bepaald na inspectie van het toestel.</p>
        <p><strong>Prijsaanpassingen</strong> kunnen plaatsvinden indien:</p>
        <ul>
          <li>Het apparaat niet overeenkomt met de opgegeven specificaties</li>
          <li>Er ongemelde schade of defecten worden aangetroffen</li>
          <li>Het apparaat niet volledig functioneel is</li>
        </ul>
        <p>U heeft altijd het recht om een aangepaste prijsopgave te weigeren en uw apparaat retour te ontvangen.</p>
      </LCSectionBody>
    </LCSection>

    <LCSection>
      <LCSectionHeader>
        <LCSectionNumber>4</LCSectionNumber>
        <LCSectionTitle>Betaling en uitbetaling</LCSectionTitle>
      </LCSectionHeader>
      <LCSectionBody>
        <p>Na acceptatie van de definitieve prijsopgave wordt de betaling binnen <strong>24 uur</strong> verwerkt via:</p>
        <ul>
          <li>Bankoverschrijving naar uw opgegeven rekeningnummer</li>
          <li>Waardebon voor gebruik in onze winkel</li>
          <li>Directe korting op een nieuwe aankoop</li>
        </ul>
        <p>Alle bedragen zijn inclusief BTW waar van toepassing.</p>
      </LCSectionBody>
    </LCSection>

    <LCSection>
      <LCSectionHeader>
        <LCSectionNumber>5</LCSectionNumber>
        <LCSectionTitle>Garantie en kwaliteit</LCSectionTitle>
      </LCSectionHeader>
      <LCSectionBody>
        <p>Wij garanderen een <strong>eerlijke en transparante</strong> waardering van uw toestel. Onze kwaliteitscontrole wordt uitgevoerd door gecertificeerde technici volgens sectorstandaarden.</p>
      </LCSectionBody>
    </LCSection>

    <LCSection>
      <LCSectionHeader>
        <LCSectionNumber>6</LCSectionNumber>
        <LCSectionTitle>Privacy en gegevensbescherming</LCSectionTitle>
      </LCSectionHeader>
      <LCSectionBody>
        <p>Uw privacy is van het grootste belang. Wij zorgen voor:</p>
        <ul>
          <li><strong>Dataverwijdering:</strong> Persoonsgegevens worden onherroepelijk gewist</li>
          <li><strong>Toepasselijke regelgeving:</strong> Conform GDPR en Belgische wetgeving</li>
        </ul>
      </LCSectionBody>
    </LCSection>

    <LCSection>
      <LCSectionHeader>
        <LCSectionNumber>7</LCSectionNumber>
        <LCSectionTitle>Aansprakelijkheid</LCSectionTitle>
      </LCSectionHeader>
      <LCSectionBody>
        <p>Phone Bros is uitsluitend aansprakelijk voor directe schade die het rechtstreekse gevolg is van een toerekenbare tekortkoming in de nakoming van onze verplichtingen.</p>
        <p>Onze aansprakelijkheid is in alle gevallen beperkt tot het bedrag van de inruilwaarde van het betreffende apparaat.</p>
      </LCSectionBody>
    </LCSection>

    <LCSection>
      <LCSectionHeader>
        <LCSectionNumber>8</LCSectionNumber>
        <LCSectionTitle>Contact en geschillen</LCSectionTitle>
      </LCSectionHeader>
      <LCSectionBody>
        <p>Voor vragen of klachten kunt u ons bereiken via <LCContactHighlight>info@phonebros.be</LCContactHighlight> of <LCContactHighlight>+32 484 89 90 32</LCContactHighlight>. Wij streven naar een vlotte en correcte afhandeling.</p>
        <p>Op deze overeenkomst is <strong>Belgisch recht</strong> van toepassing. Geschillen worden voorgelegd aan de bevoegde rechtbanken.</p>
      </LCSectionBody>
    </LCSection>
  </div>
);
};

export const PrivacyContent: React.FC = () => {
  const { language } = useLanguage();
  if (language === 'fr') {
    return (
      <div>
        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>1</LCSectionNumber>
            <LCSectionTitle>Introduction</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <p><strong>Phone Bros</strong> attache une grande importance à votre vie privée. Cet avis explique la collecte, l’utilisation et la protection de vos données.</p>
            <p>Traitement conforme au <strong>RGPD</strong> et au droit belge.</p>
          </LCSectionBody>
        </LCSection>

        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>2</LCSectionNumber>
            <LCSectionTitle>Données collectées</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <ul>
              <li><strong>Contact</strong> : nom, e‑mail, téléphone</li>
              <li><strong>Adresse</strong> : enlèvement/livraison</li>
              <li><strong>Appareil</strong> : marque, modèle, état</li>
              <li><strong>Financier</strong> : IBAN</li>
              <li><strong>Communication</strong> : e‑mails, appels, chat</li>
            </ul>
          </LCSectionBody>
        </LCSection>

        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>3</LCSectionNumber>
            <LCSectionTitle>Utilisation</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <ul>
              <li>Prestation de services</li>
              <li>Communication</li>
              <li>Traitement financier</li>
              <li>Support client</li>
              <li>Obligations légales</li>
            </ul>
          </LCSectionBody>
        </LCSection>

        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>4</LCSectionNumber>
            <LCSectionTitle>Partage</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <p>Partage uniquement avec partenaires de service, institutions financières, ou lorsque la loi l’exige.</p>
          </LCSectionBody>
        </LCSection>

        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>5</LCSectionNumber>
            <LCSectionTitle>Sécurité</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <ul>
              <li>Chiffrement SSL, serveurs sécurisés</li>
              <li>Contrôle d’accès</li>
              <li>Effacement sécurisé</li>
              <li>Audits réguliers</li>
            </ul>
          </LCSectionBody>
        </LCSection>

        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>6</LCSectionNumber>
            <LCSectionTitle>Conservation</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <ul>
              <li>Transactions : 7 ans</li>
              <li>Communication : 2 ans</li>
              <li>Données appareil : până la 1 an</li>
            </ul>
          </LCSectionBody>
        </LCSection>

        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>7</LCSectionNumber>
            <LCSectionTitle>Droits</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <ul>
              <li>Accès, rectification, effacement</li>
              <li>Limitation, portabilité, opposition</li>
            </ul>
          </LCSectionBody>
        </LCSection>

        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>8</LCSectionNumber>
            <LCSectionTitle>Contact</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <p><LCContactHighlight>info@phonebros.be</LCContactHighlight> — <LCContactHighlight>+32 484 89 90 32</LCContactHighlight>.</p>
          </LCSectionBody>
        </LCSection>
      </div>
    );
  }
  if (language === 'de') {
    return (
      <div>
        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>1</LCSectionNumber>
            <LCSectionTitle>Einführung</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <p><strong>Phone Bros</strong> schützt Ihre Daten. Diese Hinweise erklären Erhebung, Nutzung und Schutz.</p>
            <p>Verarbeitung nach <strong>DSGVO</strong> und belgischem Recht.</p>
          </LCSectionBody>
        </LCSection>
        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>2</LCSectionNumber>
            <LCSectionTitle>Welche Daten?</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <ul>
              <li>Kontakt, Adresse</li>
              <li>Geräteinformationen</li>
              <li>Finanzdaten</li>
              <li>Kommunikation</li>
            </ul>
          </LCSectionBody>
        </LCSection>
        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>3</LCSectionNumber>
            <LCSectionTitle>Nutzung</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <ul>
              <li>Leistungserbringung, Kommunikation</li>
              <li>Zahlungsabwicklung</li>
              <li>Kundensupport</li>
              <li>Gesetzliche Pflichten</li>
            </ul>
          </LCSectionBody>
        </LCSection>
        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>4</LCSectionNumber>
            <LCSectionTitle>Weitergabe</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <p>Nur an Dienstpartner, Finanzinstitute oder bei gesetzlicher Pflicht.</p>
          </LCSectionBody>
        </LCSection>
        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>5</LCSectionNumber>
            <LCSectionTitle>Sicherheit</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <ul>
              <li>SSL, sichere Server, Firewalls</li>
              <li>Zugriffskontrollen</li>
              <li>Sicheres Löschen</li>
              <li>Regelmäßige Audits</li>
            </ul>
          </LCSectionBody>
        </LCSection>
        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>6</LCSectionNumber>
            <LCSectionTitle>Aufbewahrung</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <ul>
              <li>Transaktionen: 7 Jahre</li>
              <li>Kommunikation: 2 Jahre</li>
              <li>Gerätedaten: bis 1 Jahr</li>
            </ul>
          </LCSectionBody>
        </LCSection>
        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>7</LCSectionNumber>
            <LCSectionTitle>Rechte</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <ul>
              <li>Auskunft, Berichtigung, Löschung</li>
              <li>Einschränkung, Übertragbarkeit, Widerspruch</li>
            </ul>
          </LCSectionBody>
        </LCSection>
        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>8</LCSectionNumber>
            <LCSectionTitle>Kontakt</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <p><LCContactHighlight>info@phonebros.be</LCContactHighlight> — <LCContactHighlight>+32 484 89 90 32</LCContactHighlight>.</p>
          </LCSectionBody>
        </LCSection>
      </div>
    );
  }
  if (language === 'es') {
    return (
      <div>
        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>1</LCSectionNumber>
            <LCSectionTitle>Introducción</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <p><strong>Phone Bros</strong> protege su privacidad. Este aviso explica cómo recopilamos, usamos y protegemos sus datos.</p>
            <p>Cumplimiento con <strong>RGPD</strong> y ley belga.</p>
          </LCSectionBody>
        </LCSection>
        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>2</LCSectionNumber>
            <LCSectionTitle>Datos recogidos</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <ul>
              <li>Contacto, dirección</li>
              <li>Información del dispositivo</li>
              <li>Datos financieros</li>
              <li>Comunicación</li>
            </ul>
          </LCSectionBody>
        </LCSection>
        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>3</LCSectionNumber>
            <LCSectionTitle>Uso</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <ul>
              <li>Prestación del servicio, comunicación</li>
              <li>Procesamiento financiero</li>
              <li>Atención al cliente</li>
              <li>Obligaciones legales</li>
            </ul>
          </LCSectionBody>
        </LCSection>
        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>4</LCSectionNumber>
            <LCSectionTitle>Compartición</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <p>Solo con socios de servicio, instituciones financieras o por obligación legal.</p>
          </LCSectionBody>
        </LCSection>
        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>5</LCSectionNumber>
            <LCSectionTitle>Seguridad</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <ul>
              <li>SSL, servidores seguros</li>
              <li>Control de acceso</li>
              <li>Borrado seguro</li>
              <li>Auditorías regulares</li>
            </ul>
          </LCSectionBody>
        </LCSection>
        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>6</LCSectionNumber>
            <LCSectionTitle>Conservación</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <ul>
              <li>Transacciones: 7 años</li>
              <li>Comunicación: 2 años</li>
              <li>Datos de dispositivo: hasta 1 año</li>
            </ul>
          </LCSectionBody>
        </LCSection>
        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>7</LCSectionNumber>
            <LCSectionTitle>Derechos</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <ul>
              <li>Acceso, rectificación, supresión</li>
              <li>Limitación, portabilidad, oposición</li>
            </ul>
          </LCSectionBody>
        </LCSection>
        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>8</LCSectionNumber>
            <LCSectionTitle>Contacto</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <p><LCContactHighlight>info@phonebros.be</LCContactHighlight> — <LCContactHighlight>+32 484 89 90 32</LCContactHighlight>.</p>
          </LCSectionBody>
        </LCSection>
      </div>
    );
  }
  if (language === 'bg') {
    return (
      <div>
        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>1</LCSectionNumber>
            <LCSectionTitle>Въведение</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <p><strong>Phone Bros</strong> защитава личните Ви данни. Това известие обяснява събирането, използването и защитата.</p>
            <p>Обработване съгласно <strong>GDPR</strong> и белгийското право.</p>
          </LCSectionBody>
        </LCSection>
        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>2</LCSectionNumber>
            <LCSectionTitle>Какви данни?</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <ul>
              <li>Контакт, адрес</li>
              <li>Информация за устройството</li>
              <li>Финансови данни</li>
              <li>Комуникация</li>
            </ul>
          </LCSectionBody>
        </LCSection>
        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>3</LCSectionNumber>
            <LCSectionTitle>Използване</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <ul>
              <li>Предоставяне на услуги, комуникация</li>
              <li>Плащания</li>
              <li>Поддръжка</li>
              <li>Законови задължения</li>
            </ul>
          </LCSectionBody>
        </LCSection>
        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>4</LCSectionNumber>
            <LCSectionTitle>Споделяне</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <p>Само с партньори по услуги, финансови институции или по законово изискване.</p>
          </LCSectionBody>
        </LCSection>
        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>5</LCSectionNumber>
            <LCSectionTitle>Сигурност</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <ul>
              <li>SSL, защитени сървъри</li>
              <li>Контрол на достъпа</li>
              <li>Сигурно изтриване</li>
              <li>Периодични одити</li>
            </ul>
          </LCSectionBody>
        </LCSection>
        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>6</LCSectionNumber>
            <LCSectionTitle>Съхранение</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <ul>
              <li>Транзакции: 7 г.</li>
              <li>Комуникация: 2 г.</li>
              <li>Данни за устройства: до 1 г.</li>
            </ul>
          </LCSectionBody>
        </LCSection>
        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>7</LCSectionNumber>
            <LCSectionTitle>Права</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <ul>
              <li>Достъп, корекция, изтриване</li>
              <li>Ограничаване, преносимост, възражение</li>
            </ul>
          </LCSectionBody>
        </LCSection>
        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>8</LCSectionNumber>
            <LCSectionTitle>Контакт</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <p><LCContactHighlight>info@phonebros.be</LCContactHighlight> — <LCContactHighlight>+32 484 89 90 32</LCContactHighlight>.</p>
          </LCSectionBody>
        </LCSection>
      </div>
    );
  }
  if (language === 'ro') {
    return (
      <div>
        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>1</LCSectionNumber>
            <LCSectionTitle>Introducere</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <p><strong>Phone Bros</strong> îți protejează datele. Acest anunț explică modul de colectare, utilizare și protecție.</p>
            <p>Conform <strong>GDPR</strong> și legislației belgiene.</p>
          </LCSectionBody>
        </LCSection>
        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>2</LCSectionNumber>
            <LCSectionTitle>Ce date colectăm</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <ul>
              <li>Contact, adresă</li>
              <li>Informații despre dispozitiv</li>
              <li>Date financiare</li>
              <li>Comunicare</li>
            </ul>
          </LCSectionBody>
        </LCSection>
        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>3</LCSectionNumber>
            <LCSectionTitle>Utilizare</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <ul>
              <li>Prestare servicii, comunicare</li>
              <li>Plăți</li>
              <li>Asistență clienți</li>
              <li>Obligații legale</li>
            </ul>
          </LCSectionBody>
        </LCSection>
        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>4</LCSectionNumber>
            <LCSectionTitle>Partajare</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <p>Doar cu parteneri de servicii, instituții financiare sau când legea cere.</p>
          </LCSectionBody>
        </LCSection>
        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>5</LCSectionNumber>
            <LCSectionTitle>Securitate</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <ul>
              <li>SSL, servere securizate</li>
              <li>Control acces</li>
              <li>Ștergere sigură</li>
              <li>Audituri periodice</li>
            </ul>
          </LCSectionBody>
        </LCSection>
        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>6</LCSectionNumber>
            <LCSectionTitle>Păstrare</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <ul>
              <li>Tranzacții: 7 ani</li>
              <li>Comunicare: 2 ani</li>
              <li>Date dispozitiv: până la 1 an</li>
            </ul>
          </LCSectionBody>
        </LCSection>
        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>7</LCSectionNumber>
            <LCSectionTitle>Drepturi</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <ul>
              <li>Acces, rectificare, ștergere</li>
              <li>Limitare, portabilitate, opoziție</li>
            </ul>
          </LCSectionBody>
        </LCSection>
        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>8</LCSectionNumber>
            <LCSectionTitle>Contact</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <p><LCContactHighlight>info@phonebros.be</LCContactHighlight> — <LCContactHighlight>+32 484 89 90 32</LCContactHighlight>.</p>
          </LCSectionBody>
        </LCSection>
      </div>
    );
  }
  if (language === 'sk') {
    return (
      <div>
        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>1</LCSectionNumber>
            <LCSectionTitle>Úvod</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <p><strong>Phone Bros</strong> chráni vaše údaje. Toto oznámenie vysvetľuje zber, použitie a ochranu.</p>
            <p>Spracovanie podľa <strong>GDPR</strong> a belgického práva.</p>
          </LCSectionBody>
        </LCSection>
        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>2</LCSectionNumber>
            <LCSectionTitle>Aké údaje?</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <ul>
              <li>Kontakt, adresa</li>
              <li>Informácie o zariadení</li>
              <li>Finančné údaje</li>
              <li>Komunikácia</li>
            </ul>
          </LCSectionBody>
        </LCSection>
        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>3</LCSectionNumber>
            <LCSectionTitle>Použitie</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <ul>
              <li>Poskytovanie služieb, komunikácia</li>
              <li>Platby</li>
              <li>Podpora</li>
              <li>Právne povinnosti</li>
            </ul>
          </LCSectionBody>
        </LCSection>
        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>4</LCSectionNumber>
            <LCSectionTitle>Zdieľanie</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <p>Len s partnermi služieb, finančnými inštitúciami alebo ak to vyžaduje zákon.</p>
          </LCSectionBody>
        </LCSection>
        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>5</LCSectionNumber>
            <LCSectionTitle>Bezpečnosť</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <ul>
              <li>SSL, zabezpečené servery</li>
              <li>Kontrola prístupu</li>
              <li>Bezpečné mazanie</li>
              <li>Pravidelné audity</li>
            </ul>
          </LCSectionBody>
        </LCSection>
        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>6</LCSectionNumber>
            <LCSectionTitle>Uchovávanie</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <ul>
              <li>Transakcie: 7 rokov</li>
              <li>Komunikácia: 2 roky</li>
              <li>Údaje o zariadení: do 1 roka</li>
            </ul>
          </LCSectionBody>
        </LCSection>
        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>7</LCSectionNumber>
            <LCSectionTitle>Práva</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <ul>
              <li>Prístup, oprava, vymazanie</li>
              <li>Obmedzenie, prenos, námietka</li>
            </ul>
          </LCSectionBody>
        </LCSection>
        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>8</LCSectionNumber>
            <LCSectionTitle>Kontakt</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <p><LCContactHighlight>info@phonebros.be</LCContactHighlight> — <LCContactHighlight>+32 484 89 90 32</LCContactHighlight>.</p>
          </LCSectionBody>
        </LCSection>
      </div>
    );
  }
  if (language === 'tr') {
    return (
      <div>
        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>1</LCSectionNumber>
            <LCSectionTitle>Giriş</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <p><strong>Phone Bros</strong> gizliliğinize ve kişisel verilerinizin korunmasına büyük önem verir. Bu bildirim, verilerinizi nasıl topladığımızı, kullandığımızı ve koruduğumuzu açıklar.</p>
            <p>Kişisel verileri <strong>GDPR</strong> ve Belçika gizlilik mevzuatına uygun şekilde işleriz.</p>
          </LCSectionBody>
        </LCSection>

        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>2</LCSectionNumber>
            <LCSectionTitle>Hangi verileri topluyoruz?</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <p>Takas ve satış hizmetleri için şu verileri toplarız:</p>
            <ul>
              <li><strong>İletişim:</strong> İsim, e‑posta, telefon</li>
              <li><strong>Adres:</strong> Kapıdan alma ve teslimat</li>
              <li><strong>Cihaz bilgileri:</strong> Marka, model, durum</li>
              <li><strong>Finansal:</strong> Ödeme için IBAN</li>
              <li><strong>İletişim kayıtları:</strong> E‑posta, telefon veya chat</li>
            </ul>
          </LCSectionBody>
        </LCSection>

        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>3</LCSectionNumber>
            <LCSectionTitle>Verileri ne için kullanıyoruz?</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <p>Verileriniz yalnızca şu amaçlarla kullanılır:</p>
            <ul>
              <li><strong>Hizmet sunumu:</strong> Takas/satış işlemleri</li>
              <li><strong>İletişim:</strong> Sipariş ve servis bilgilendirme</li>
              <li><strong>Finansal işlemler:</strong> Ödeme</li>
              <li><strong>Müşteri desteği:</strong> Destek ve çözüm</li>
              <li><strong>Yasal yükümlülük:</strong> Muhasebe ve vergisel işlemler</li>
            </ul>
            <p>Açık rızanız olmadan verilerinizi pazarlama için <strong>kullanmayız</strong>.</p>
          </LCSectionBody>
        </LCSection>

        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>4</LCSectionNumber>
            <LCSectionTitle>Üçüncü taraflarla paylaşım</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <p>Verileriniz sadece şu durumlarda paylaşılır:</p>
            <ul>
              <li><strong>Hizmet ortakları:</strong> Kapıdan alma/teslimat (gizlilik güvenceleriyle)</li>
              <li><strong>Finans kurumları:</strong> Ödeme işlemleri</li>
              <li><strong>Yasal zorunluluk:</strong> Mevzuat gereği</li>
            </ul>
          </LCSectionBody>
        </LCSection>

        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>5</LCSectionNumber>
            <LCSectionTitle>Veri güvenliği</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <p>Verilerinizi korumak için kapsamlı önlemler alırız:</p>
            <ul>
              <li><strong>Teknik güvenlik:</strong> SSL, güvenli sunucular, güvenlik duvarları</li>
              <li><strong>Erişim kontrolü:</strong> Yetkili personel erişimi</li>
              <li><strong>Veri silme:</strong> Cihazlardan güvenli silme</li>
              <li><strong>Düzenli denetimler:</strong> Güvenlik tedbirlerinin kontrolü</li>
            </ul>
          </LCSectionBody>
        </LCSection>

        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>6</LCSectionNumber>
            <LCSectionTitle>Saklama süresi</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <p>Veriler, gerekli olandan uzun süre tutulmaz:</p>
            <ul>
              <li><strong>İşlem verileri:</strong> 7 yıl (yasal)</li>
              <li><strong>İletişim:</strong> Son temastan 2 yıl</li>
              <li><strong>Cihaz verileri:</strong> Hizmetten sonra 1 yıla kadar</li>
            </ul>
          </LCSectionBody>
        </LCSection>

        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>7</LCSectionNumber>
            <LCSectionTitle>Haklarınız</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <p>Kişisel verilerinizle ilgili şu haklara sahipsiniz:</p>
            <ul>
              <li><strong>Erişim:</strong> Hakkınızdaki verileri talep etme</li>
              <li><strong>Düzeltme:</strong> Hatalı verilerin düzeltilmesi</li>
              <li><strong>Silme:</strong> Verilerin silinmesini talep etme (mümkünse)</li>
              <li><strong>Kısıtlama:</strong> İşlemenin sınırlandırılması</li>
              <li><strong>Taşınabilirlik:</strong> Verilerin yapılandırılmış formatta alınması</li>
              <li><strong>İtiraz:</strong> Belirli işlemlere itiraz</li>
            </ul>
          </LCSectionBody>
        </LCSection>

        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>8</LCSectionNumber>
            <LCSectionTitle>İletişim ve şikayet</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <p>Gizlilikle ilgili talepleriniz için <LCContactHighlight>info@phonebros.be</LCContactHighlight> veya <LCContactHighlight>+32 484 89 90 32</LCContactHighlight> üzerinden bize ulaşabilirsiniz.</p>
            <p>Gerekirse <strong>Veri Koruma Otoritesi</strong>'ne şikayette bulunabilirsiniz.</p>
          </LCSectionBody>
        </LCSection>
      </div>
    );
  }
  if (language !== 'nl') {
    return (
      <div>
        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>1</LCSectionNumber>
            <LCSectionTitle>Introduction</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <p><strong>Phone Bros</strong> highly values your privacy and the protection of your personal data. This privacy notice explains how we collect, use and protect your data.</p>
            <p>We process personal data in accordance with the <strong>General Data Protection Regulation (GDPR)</strong> and Belgian privacy legislation.</p>
          </LCSectionBody>
        </LCSection>

        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>2</LCSectionNumber>
            <LCSectionTitle>What data do we collect?</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <p>For our trade‑in and selling services we collect the following data:</p>
            <ul>
              <li><strong>Contact details:</strong> Name, email address, phone number</li>
              <li><strong>Address details:</strong> For pickup and delivery services</li>
              <li><strong>Device information:</strong> Brand, model, condition of your device</li>
              <li><strong>Financial details:</strong> Bank account for payout</li>
              <li><strong>Communication:</strong> Messages via email, phone or chat</li>
            </ul>
          </LCSectionBody>
        </LCSection>

        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>3</LCSectionNumber>
            <LCSectionTitle>How do we use your data?</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <p>Your personal data is used solely for:</p>
            <ul>
              <li><strong>Service delivery:</strong> Executing trade‑in and selling transactions</li>
              <li><strong>Communication:</strong> Contact about your order and service updates</li>
              <li><strong>Financial processing:</strong> Payout of trade‑in value</li>
              <li><strong>Customer support:</strong> Assistance and troubleshooting</li>
              <li><strong>Legal obligations:</strong> Accounting and tax filings</li>
            </ul>
            <p>We <strong>never</strong> use your data for marketing without your explicit consent.</p>
          </LCSectionBody>
        </LCSection>

        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>4</LCSectionNumber>
            <LCSectionTitle>Sharing with third parties</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <p>We only share your personal data in the following cases:</p>
            <ul>
              <li><strong>Service partners:</strong> For pickup and delivery (with privacy safeguards)</li>
              <li><strong>Financial institutions:</strong> For payment processing</li>
              <li><strong>Legal obligation:</strong> When required by law or regulation</li>
            </ul>
            <p>All external parties are contractually bound to keep your data confidential.</p>
          </LCSectionBody>
        </LCSection>

        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>5</LCSectionNumber>
            <LCSectionTitle>Data security</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <p>We take extensive measures to protect your data:</p>
            <ul>
              <li><strong>Technical security:</strong> SSL encryption, secured servers and firewalls</li>
              <li><strong>Access control:</strong> Only authorised staff have access</li>
              <li><strong>Data wiping:</strong> Secure deletion from traded‑in devices</li>
              <li><strong>Regular audits:</strong> Review of our security measures</li>
            </ul>
          </LCSectionBody>
        </LCSection>

        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>6</LCSectionNumber>
            <LCSectionTitle>Retention period</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <p>We do not retain your data longer than necessary:</p>
            <ul>
              <li><strong>Transaction data:</strong> 7 years (legal obligation)</li>
              <li><strong>Communication:</strong> 2 years after last contact</li>
              <li><strong>Device data:</strong> Up to 1 year after service completion</li>
            </ul>
          </LCSectionBody>
        </LCSection>

        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>7</LCSectionNumber>
            <LCSectionTitle>Your rights</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <p>You have the following rights regarding your personal data:</p>
            <ul>
              <li><strong>Access:</strong> Request which data we hold about you</li>
              <li><strong>Rectification:</strong> Correct inaccurate data</li>
              <li><strong>Erasure:</strong> Delete your data (where possible)</li>
              <li><strong>Restriction:</strong> Limit certain processing</li>
              <li><strong>Portability:</strong> Receive your data in a structured format</li>
              <li><strong>Objection:</strong> Object to certain processing activities</li>
            </ul>
          </LCSectionBody>
        </LCSection>

        <LCSection>
          <LCSectionHeader>
            <LCSectionNumber>8</LCSectionNumber>
            <LCSectionTitle>Contact and complaints</LCSectionTitle>
          </LCSectionHeader>
          <LCSectionBody>
            <p>For privacy questions or to exercise your rights, contact <LCContactHighlight>info@phonebros.be</LCContactHighlight> or <LCContactHighlight>+32 484 89 90 32</LCContactHighlight>. We will respond as soon as possible.</p>
            <p>You may also lodge a complaint with the <strong>Data Protection Authority</strong> if you believe we process your data unlawfully.</p>
          </LCSectionBody>
        </LCSection>
      </div>
    );
  }

  return (
    <div>
      <LCSection>
        <LCSectionHeader>
          <LCSectionNumber>1</LCSectionNumber>
          <LCSectionTitle>Introductie</LCSectionTitle>
        </LCSectionHeader>
        <LCSectionBody>
          <p><strong>Phone Bros</strong> hecht grote waarde aan uw privacy en de bescherming van uw persoonsgegevens. Deze privacyverklaring licht toe hoe wij uw gegevens verzamelen, gebruiken en beschermen.</p>
          <p>Wij verwerken persoonsgegevens conform de <strong>Algemene Verordening Gegevensbescherming (GDPR)</strong> en de Belgische privacywetgeving.</p>
        </LCSectionBody>
      </LCSection>

      <LCSection>
        <LCSectionHeader>
          <LCSectionNumber>2</LCSectionNumber>
          <LCSectionTitle>Welke gegevens verzamelen wij?</LCSectionTitle>
        </LCSectionHeader>
        <LCSectionBody>
          <p>Voor onze inruil- en verkoopdiensten verzamelen wij de volgende gegevens:</p>
          <ul>
            <li><strong>Contactgegevens:</strong> Naam, e-mailadres, telefoonnummer</li>
            <li><strong>Adresgegevens:</strong> Voor ophaal- en leveringsdiensten</li>
            <li><strong>Apparaatinformatie:</strong> Merk, model, staat van uw in te ruilen apparaat</li>
            <li><strong>Financiële gegevens:</strong> Bankrekeningnummer voor uitbetaling</li>
            <li><strong>Communicatie:</strong> Berichten via e-mail, telefoon of chat</li>
          </ul>
        </LCSectionBody>
      </LCSection>

      <LCSection>
        <LCSectionHeader>
          <LCSectionNumber>3</LCSectionNumber>
          <LCSectionTitle>Waarvoor gebruiken wij uw gegevens?</LCSectionTitle>
        </LCSectionHeader>
        <LCSectionBody>
          <p>Uw persoonsgegevens worden uitsluitend gebruikt voor:</p>
          <ul>
            <li><strong>Dienstverlening:</strong> Uitvoeren van inruil- en verkooptransacties</li>
            <li><strong>Communicatie:</strong> Contact over uw opdracht en service-updates</li>
            <li><strong>Financiële afhandeling:</strong> Uitbetaling van inruilwaarde</li>
            <li><strong>Klantenservice:</strong> Ondersteuning en probleemoplossing</li>
            <li><strong>Wettelijke verplichtingen:</strong> Boekhouding en belastingaangiften</li>
          </ul>
          <p>Wij gebruiken uw gegevens <strong>nooit</strong> voor marketing zonder uw expliciete toestemming.</p>
        </LCSectionBody>
      </LCSection>

      <LCSection>
        <LCSectionHeader>
          <LCSectionNumber>4</LCSectionNumber>
          <LCSectionTitle>Delen met derden</LCSectionTitle>
        </LCSectionHeader>
        <LCSectionBody>
          <p>Wij delen uw persoonsgegevens uitsluitend in de volgende gevallen:</p>
          <ul>
            <li><strong>Servicepartners:</strong> Voor ophaal- en leveringsdiensten (met privacy waarborgen)</li>
            <li><strong>Financiële instellingen:</strong> Voor betalingsverwerking</li>
            <li><strong>Wettelijke verplichting:</strong> Wanneer dit verplicht is door wet- of regelgeving</li>
          </ul>
          <p>Alle externe partijen zijn contractueel verplicht om uw gegevens vertrouwelijk te behandelen.</p>
        </LCSectionBody>
      </LCSection>

      <LCSection>
        <LCSectionHeader>
          <LCSectionNumber>5</LCSectionNumber>
          <LCSectionTitle>Gegevensbeveiliging</LCSectionTitle>
        </LCSectionHeader>
        <LCSectionBody>
          <p>Wij nemen uitgebreide maatregelen om uw gegevens te beschermen:</p>
          <ul>
            <li><strong>Technische beveiliging:</strong> SSL-encryptie, beveiligde servers en firewalls</li>
            <li><strong>Toegangscontrole:</strong> Alleen geautoriseerd personeel heeft toegang</li>
            <li><strong>Data-wissing:</strong> Veilige verwijdering van gegevens van ingeruilde apparaten</li>
            <li><strong>Regelmatige audits:</strong> Controle van onze beveiligingsmaatregelen</li>
          </ul>
        </LCSectionBody>
      </LCSection>

      <LCSection>
        <LCSectionHeader>
          <LCSectionNumber>6</LCSectionNumber>
          <LCSectionTitle>Bewaarperiode</LCSectionTitle>
        </LCSectionHeader>
        <LCSectionBody>
          <p>Wij bewaren uw gegevens niet langer dan noodzakelijk:</p>
          <ul>
            <li><strong>Transactiegegevens:</strong> 7 jaar (wettelijke verplichting)</li>
            <li><strong>Communicatie:</strong> 2 jaar na laatste contact</li>
            <li><strong>Apparaatgegevens:</strong> Tot 1 jaar na afronding service</li>
          </ul>
        </LCSectionBody>
      </LCSection>

      <LCSection>
        <LCSectionHeader>
          <LCSectionNumber>7</LCSectionNumber>
          <LCSectionTitle>Uw rechten</LCSectionTitle>
        </LCSectionHeader>
        <LCSectionBody>
          <p>U heeft de volgende rechten betreffende uw persoonsgegevens:</p>
          <ul>
            <li><strong>Inzagerecht:</strong> Opvragen welke gegevens wij van u hebben</li>
            <li><strong>Rectificatie:</strong> Correctie van onjuiste gegevens</li>
            <li><strong>Wissing:</strong> Verwijdering van uw gegevens (waar mogelijk)</li>
            <li><strong>Beperking:</strong> Beperking van de verwerking</li>
            <li><strong>Overdraagbaarheid:</strong> Uw gegevens in een gestructureerd formaat ontvangen</li>
            <li><strong>Bezwaar:</strong> Bezwaar maken tegen bepaalde verwerkingen</li>
          </ul>
        </LCSectionBody>
      </LCSection>

      <LCSection>
        <LCSectionHeader>
          <LCSectionNumber>8</LCSectionNumber>
          <LCSectionTitle>Contact en klachten</LCSectionTitle>
        </LCSectionHeader>
        <LCSectionBody>
          <p>Voor vragen over uw privacy of het uitoefenen van uw rechten kunt u terecht op <LCContactHighlight>info@phonebros.be</LCContactHighlight> of <LCContactHighlight>+32 484 89 90 32</LCContactHighlight>. Wij beantwoorden uw verzoek zo spoedig mogelijk.</p>
          <p>U kan tevens een klacht indienen bij de <strong>Gegevensbeschermingsautoriteit</strong> indien u meent dat wij uw gegevens niet correct verwerken.</p>
        </LCSectionBody>
      </LCSection>
    </div>
  );
};

export default TermsContent;


